import { DialogModule } from '@angular/cdk/dialog';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AngularPinturaModule } from '@pqina/angular-pintura';
import { EditorModule } from '@tinymce/tinymce-angular';
import { AlertModule } from 'webcommon/ap-ngx-bootstrap/alert';

import {
  CollapseModule,
} from 'ngx-bootstrap/collapse';
import {
  BsDatepickerModule,
} from 'webcommon/ap-ngx-bootstrap/datepicker';

import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySliderModule as MatSliderModule } from '@angular/material/legacy-slider';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';

import {
  ReactiveFormsModule,
} from '@angular/forms';

import { MatGridListModule } from '@angular/material/grid-list';
import { SafeHtmlPipe } from 'webcommon/core-ui/pipes-ng1520';
import { ApActionButtonsComponent } from './ap-action-buttons';
import {
  ApAddressComponent,
} from './ap-address';
import {
  ApAlertComponent,
} from './ap-alert';
import {
  ApComboboxComponent,
} from './ap-combobox';
import {
  ApUIConfigurationSaveHeaderComponent,
} from './ap-configuration';
import {
  ApDatePickerComponent, ApDatePickerInputDirective,
  FromServerDateStringPipe,
  ApDatePickerServerDateDirective,
  ApDatePickerDateOnlyDirective,
} from './ap-date-picker';
import {
  ApDatePickerNextPrevComponent,
} from './ap-datepicker-next-prev';
import { ApDocumentEditorComponent } from './ap-document-editor';
import { ApDocumentEnhancedViewerComponent } from './ap-document-enhanced-viewer';
import { ApDropdownItemMenuComponent } from './ap-dropdown-item-menu';
import { ApDynamicComponentEditorComponent, ApDynamicComponentViewerComponent, FlexBasisPercentPipe, FlexBasisPipe, FlexGrowPipe, MinWidthPipe } from './ap-dynamic-component';
import { ApEnhancedImageViewerComponent } from './ap-enhanced-image-viewer';
import {
  ApErrorMessageComponent,
} from './ap-error-message';
import {
  ApFileUploadComponent,
} from './ap-file-upload';
import {
  ApFileUploadModalComponent,
} from './ap-file-upload-modal';
import {
  ApFileUploadWithListComponent,
  ApFileUploadWithListModalComponent,
} from './ap-file-upload-with-list';
import { ApIframeComponent, ApIframeDirective } from './ap-iframe';
import { ApIframeModalComponent } from './ap-iframe-modal';
import { ApImageEditorComponent } from './ap-image-editor';
import { ApInputClearComponent } from './ap-input-clear';
import {
  ApLoadingContentComponent,
} from './ap-loading-content';
import {
  ApNumericUpDownComponent,
} from './ap-numeric-up-down';
import { ApPillComponent } from './ap-pill';
import {
  ApSearchListComponent,
} from './ap-search-list';
import {
  ApSelectComponent,
} from './ap-select';
import { ApSliderComponent } from './ap-slider';
import { ApTileComponent } from './ap-tile/ap-tile.component';
import {
  ApTimeFrameComponent,
  ApTimeFrameSelectorModalComponent,
} from './ap-timeframe';
import {
  ApWarningModalComponent,
  ApWarningUnsavedChangesModalComponent,
} from './ap-warning';
import { ApCVAViewToModelChangeDirective } from './base-control';
import {
  CdkMixedOrientationChildDirective,
  CdkMixedOrientationParentDirective,
  CdkMixedOrientationPlaceholderDirective,
} from './cdk-mixed-orientation';
import {
  ApAutofocusDirective,
  ApCheckboxDirective,
  ApColorBorderDirective,
  ApDecimalPlacesDirective,
  ApDisableControlDirective,
  ApDynamicSizeFromAncestorDirective,
  ApFieldInvalidDirective,
  ApFixedHeaderDirective,
  ApFormFocusDirective,
  ApHeightDirective,
  ApLongTouchDirective,
  ApMaxHeightDirective,
  ApMinMaxValueDirective,
  ApNumberValidationDirective,
  ApPhoneNumberMaskDirective,
  ApReactiveSsnFieldDirective,
  ApRequiredDirective,
  ApRequiredFromMetadataDirective,
  ApResizedDirective,
  ApSpinnerDirective,
  ApZoomableDirective,
  ContentEditableDirective,
  NoSpecialCharactersDirective,
} from './directives/index';
import {
  ApDocumentViewerComponent,
  ApDocumentViewerModalComponent,
  ApDocumentViewerV2Component,
  ApEnhancedDocumentViewerComponent,
  ApEnhancedDocumentViewerV2Component,
  ApFoxitViewerDirective,
  ApImageViewerComponent,
  ApPdfComponent,
  ApSaveToFileDirective,
  ApSignPdfComponent,
} from './document-viewing';
import {
  ToCurrentlyLoadingPipe,
  ToErrorMessagesPipe,
  ToLoadedValuePipe,
  ToLoadingFailurePipe,
} from './loading-util';
import { IgnorePageSizeDirective } from './material';
import {
  ApPhoneNumberPipe,
  BypassSecurityResourceUrlPipe,
  CoerceToNullPipe,
  HtmlIdValueSanitizerPipe,
  InactiveFilterPipe, IsNilPipe, IsNotNilPipe,
  Nl2brPipe,
} from './pipes/index';
import {
  ApCheckSizeDirective,
} from './virtual-scrolling';

const ajsComponents = [
  ApSignPdfComponent,
];

const documentViewingExports = [
  ApDocumentViewerComponent,
  ApDocumentViewerV2Component,
  ApEnhancedDocumentViewerComponent,
  ApEnhancedDocumentViewerV2Component,
  ApImageViewerComponent,
  ApEnhancedImageViewerComponent,
  ApPdfComponent,
  ApSaveToFileDirective,
];

const modalComponents = [
  ApDocumentViewerModalComponent,
  ApFileUploadModalComponent,
  ApFileUploadWithListModalComponent,
  ApIframeModalComponent,
  ApTimeFrameSelectorModalComponent,
  ApWarningModalComponent,
  ApWarningUnsavedChangesModalComponent,
];

const angularMaterialComponents = [
  MatBadgeModule,
  MatCheckboxModule,
  MatAutocompleteModule,
  MatButtonModule,
  MatCardModule,
  MatCheckboxModule,
  MatChipsModule,
  MatDividerModule,
  MatExpansionModule,
  MatFormFieldModule,
  MatGridListModule,
  MatIconModule,
  MatInputModule,
  MatMenuModule,
  MatSelectModule,
  MatSliderModule,
  MatSnackBarModule,
  MatRadioModule,
  MatTableModule,
  MatTooltipModule,
  MatTabsModule,
  DialogModule,
  ReactiveFormsModule,
  MatPaginatorModule,
  MatSlideToggleModule,
];

const myExports = [
  ...ajsComponents,
  ...documentViewingExports,
  ...modalComponents,
  ApAddressComponent,
  ApAlertComponent,
  ApAutofocusDirective,
  ApCheckboxDirective,
  ApCheckSizeDirective,
  ApColorBorderDirective,
  ApComboboxComponent,
  ApDatePickerComponent,
  ApDatePickerInputDirective,
  FromServerDateStringPipe,
  ApDatePickerServerDateDirective,
  ApDatePickerDateOnlyDirective,
  ApDatePickerNextPrevComponent,
  ApDecimalPlacesDirective,
  ApDocumentEditorComponent,
  ApDocumentEnhancedViewerComponent,
  ApDocumentViewerComponent,
  ApDropdownItemMenuComponent,
  ApDynamicComponentViewerComponent,
  ApDynamicComponentEditorComponent,
  ApDynamicSizeFromAncestorDirective,
  ApErrorMessageComponent,
  ApFieldInvalidDirective,
  ApFileUploadComponent,
  ApFileUploadModalComponent,
  ApFileUploadWithListComponent,
  ApFixedHeaderDirective,
  ApFormFocusDirective,
  ApHeightDirective,
  ApIframeComponent,
  ApIframeDirective,
  ApImageEditorComponent,
  ApEnhancedImageViewerComponent,
  ApLoadingContentComponent,
  ApLongTouchDirective,
  ApMaxHeightDirective,
  ApMinMaxValueDirective,
  ApNumberValidationDirective,
  ApNumericUpDownComponent,
  ApPhoneNumberMaskDirective,
  ApPhoneNumberPipe,
  ApPillComponent,
  ApReactiveSsnFieldDirective,
  ApRequiredDirective,
  ApRequiredFromMetadataDirective,
  ApResizedDirective,
  ApSearchListComponent,
  ApSelectComponent,
  ApSliderComponent,
  ApSpinnerDirective,
  ApTileComponent,
  ApTimeFrameComponent,
  ApTimeFrameSelectorModalComponent,
  ApUIConfigurationSaveHeaderComponent,
  ApWarningModalComponent,
  ApZoomableDirective,
  CoerceToNullPipe,
  ContentEditableDirective,
  InactiveFilterPipe,
  Nl2brPipe,
  ToCurrentlyLoadingPipe,
  ToErrorMessagesPipe,
  ToLoadedValuePipe,
  ToLoadingFailurePipe,
  ApDisableControlDirective,
  ApInputClearComponent,
  ApCVAViewToModelChangeDirective,
  HtmlIdValueSanitizerPipe,
  IsNilPipe,
  IsNotNilPipe,
  ApActionButtonsComponent,
  NoSpecialCharactersDirective,
  FlexGrowPipe,
  FlexBasisPipe,
  FlexBasisPercentPipe,
  MinWidthPipe,
];

@NgModule({
  declarations: myExports,
  exports: [
    ...angularMaterialComponents,
    CommonModule,
    AlertModule,
    BsDatepickerModule,
    CollapseModule,
    DragDropModule,
    EditorModule,
    FormsModule,
    ScrollingModule,
    TranslateModule,
    ...myExports,
    SafeHtmlPipe,
  ],
  imports: [
    ...angularMaterialComponents,
    CommonModule,
    AlertModule,
    BsDatepickerModule,
    CollapseModule,
    EditorModule,
    FormsModule,
    ScrollingModule,
    TranslateModule,
    DragDropModule,
    BypassSecurityResourceUrlPipe,
    ApFoxitViewerDirective,
    CdkMixedOrientationParentDirective,
    CdkMixedOrientationChildDirective,
    CdkMixedOrientationPlaceholderDirective,
    AngularPinturaModule,
    SafeHtmlPipe,
    IgnorePageSizeDirective,
  ],
})
export class CoreUiModule {
}
